/**
 * NavigatorX Blocklist Manager
 *
 * Manages blocked emails and phone numbers:
 * - View all blocked entries (hashed for privacy)
 * - Block new emails/phones with reason
 * - Unblock entries with confirmation
 * - Stats overview
 *
 * All entries are SHA-256 hashed before storage.
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  ShieldBan,
  Mail,
  Phone,
  Plus,
  Trash2,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Search,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import {
  blockEmail,
  unblockEmail,
  blockPhone,
  unblockPhone,
  getBlockedEmails,
  getBlockedPhones
} from '../../../utils/services/navigatorXService';
import type { BlockedEmail, BlockedPhone } from '../../../types/navigatorx';

// ============================================================================
// MODULE-LEVEL PURE FUNCTIONS
// ============================================================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return 'N/A';
  if (ts instanceof Timestamp) return ts.toDate().toLocaleDateString() + ' ' + ts.toDate().toLocaleTimeString();
  if (ts instanceof Date) return ts.toLocaleDateString() + ' ' + ts.toLocaleTimeString();
  if (typeof ts === 'string') return new Date(ts).toLocaleDateString() + ' ' + new Date(ts).toLocaleTimeString();
  if (typeof ts === 'number') return new Date(ts).toLocaleDateString() + ' ' + new Date(ts).toLocaleTimeString();
  return 'N/A';
}

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return hash.substring(0, 8) + '...' + hash.substring(hash.length - 8);
}

// ============================================================================
// TYPES
// ============================================================================

interface BlocklistManagerProps {
  embedded?: boolean;
}

type ActiveTab = 'emails' | 'phones';

// ============================================================================
// COMPONENT
// ============================================================================

export default function BlocklistManager({ embedded = false }: BlocklistManagerProps) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('emails');

  const [blockedEmails, setBlockedEmails] = useState<BlockedEmail[]>([]);
  const [blockedPhones, setBlockedPhones] = useState<BlockedPhone[]>([]);

  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockInput, setBlockInput] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);

  const [confirmUnblock, setConfirmUnblock] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [emails, phones] = await Promise.all([
        getBlockedEmails(),
        getBlockedPhones()
      ]);
      setBlockedEmails(emails as BlockedEmail[]);
      setBlockedPhones(phones as BlockedPhone[]);
    } catch (error) {
      console.error('Error loading blocklist data:', error);
      toast.error('Failed to load blocklist data');
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!blockInput.trim() || !blockReason.trim()) {
      toast.error('Both value and reason are required');
      return;
    }

    setBlocking(true);
    try {
      if (activeTab === 'emails') {
        await blockEmail(blockInput.trim().toLowerCase(), blockReason.trim(), adminId);
        toast.success('Email blocked successfully');
      } else {
        await blockPhone(blockInput.trim(), blockReason.trim(), adminId);
        toast.success('Phone blocked successfully');
      }
      setBlockInput('');
      setBlockReason('');
      setShowBlockForm(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblockEmail = async (emailHash: string) => {
    try {
      await unblockEmail(emailHash, adminId);
      toast.success('Email unblocked');
      setConfirmUnblock(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUnblockPhone = async (phoneHash: string) => {
    try {
      await unblockPhone(phoneHash, adminId);
      toast.success('Phone unblocked');
      setConfirmUnblock(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const filteredEmails = blockedEmails.filter(e =>
    !searchQuery || e.emailHash.includes(searchQuery) || e.reason.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPhones = blockedPhones.filter(p =>
    !searchQuery || p.phoneHash.includes(searchQuery) || p.reason.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Blocklist Manager</h2>
            <p className="text-gray-500">Manage blocked emails and phone numbers</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className={blockedEmails.length > 0 ? 'border-red-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Blocked Emails</p>
                <p className="text-2xl font-bold text-gray-700">{blockedEmails.length}</p>
              </div>
              <Mail className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card className={blockedPhones.length > 0 ? 'border-red-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Blocked Phones</p>
                <p className="text-2xl font-bold text-gray-700">{blockedPhones.length}</p>
              </div>
              <Phone className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs + Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 border-b">
          <button
            onClick={() => { setActiveTab('emails'); setShowBlockForm(false); setConfirmUnblock(null); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'emails' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Mail className="h-4 w-4" />
            Emails ({blockedEmails.length})
          </button>
          <button
            onClick={() => { setActiveTab('phones'); setShowBlockForm(false); setConfirmUnblock(null); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'phones' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Phone className="h-4 w-4" />
            Phones ({blockedPhones.length})
          </button>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search hash or reason..."
              className="pl-8 text-sm w-48"
            />
          </div>
          <Button
            size="sm"
            onClick={() => setShowBlockForm(!showBlockForm)}
            className="bg-red-500 hover:bg-red-600"
          >
            <Plus className="h-4 w-4 mr-1" />
            Block {activeTab === 'emails' ? 'Email' : 'Phone'}
          </Button>
        </div>
      </div>

      {/* Block Form */}
      {showBlockForm && (
        <Card className="mb-4 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
              <p className="text-sm text-red-600">
                {activeTab === 'emails'
                  ? 'The email will be SHA-256 hashed before storage. Users with this email will be unable to register.'
                  : 'The phone number will be SHA-256 hashed before storage. Users with this phone will be unable to register.'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  {activeTab === 'emails' ? 'Email Address' : 'Phone Number'}
                </label>
                <Input
                  value={blockInput}
                  onChange={(e) => setBlockInput(e.target.value)}
                  placeholder={activeTab === 'emails' ? 'user@example.com' : '+1234567890'}
                  type={activeTab === 'emails' ? 'email' : 'tel'}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Reason</label>
                <Input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="e.g. Fraud, spam, abuse..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowBlockForm(false); setBlockInput(''); setBlockReason(''); }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleBlock}
                disabled={blocking || !blockInput.trim() || !blockReason.trim()}
                className="bg-red-500 hover:bg-red-600"
              >
                {blocking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldBan className="h-4 w-4 mr-1" />}
                Block
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email List */}
      {activeTab === 'emails' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Blocked Emails
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredEmails.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchQuery ? 'No matching blocked emails' : 'No blocked emails'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEmails.map(entry => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-gray-700">
                          {truncateHash(entry.emailHash)}
                        </code>
                        <Badge variant="outline" className="text-xs">SHA-256</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>Reason: {entry.reason}</span>
                        <span>&bull;</span>
                        <span>{formatTimestamp(entry.blockedAt)}</span>
                        <span>&bull;</span>
                        <span>By: {entry.blockedBy.substring(0, 8)}...</span>
                      </div>
                    </div>
                    <div>
                      {confirmUnblock === entry.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-500">Unblock?</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnblockEmail(entry.emailHash)}
                            className="text-red-600 border-red-200"
                          >
                            Yes
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmUnblock(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmUnblock(entry.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phone List */}
      {activeTab === 'phones' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Blocked Phones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPhones.length === 0 ? (
              <div className="text-center py-8">
                <Phone className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchQuery ? 'No matching blocked phones' : 'No blocked phones'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPhones.map(entry => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-gray-700">
                          {truncateHash(entry.phoneHash)}
                        </code>
                        <Badge variant="outline" className="text-xs">SHA-256</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>Reason: {entry.reason}</span>
                        <span>&bull;</span>
                        <span>{formatTimestamp(entry.blockedAt)}</span>
                        <span>&bull;</span>
                        <span>By: {entry.blockedBy.substring(0, 8)}...</span>
                      </div>
                    </div>
                    <div>
                      {confirmUnblock === entry.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-500">Unblock?</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnblockPhone(entry.phoneHash)}
                            className="text-red-600 border-red-200"
                          >
                            Yes
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmUnblock(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmUnblock(entry.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
